import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useHistory, useLocation, Prompt } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { TextareaInput } from '@components/atoms/TextareaInput';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import GoogleAddressInput from '@components/atoms/GoogleAddressInput';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { InputCurrency } from '@components/atoms/InputCurrency';
import { Wizard, WizardStep } from '@components/molecules/Wizard';
import { SelectTemplateModal } from '@components/organisms/SelectTemplateModal';
import { AiPlanGenerator } from '@components/organisms/AiPlanGenerator';
import { InProgressAiPlans } from '@components/molecules/InProgressAiPlans';
import { TaskTemplate } from '@models/taskTemplate.model';
import { useClients, useEmployees } from '@hooks/api';
import { useCreateProject } from '@hooks/api/projects';
import { useImportTaskTemplate } from '@hooks/api/taskTemplates/useImportTaskTemplate';
import { useAiPlanningAvailable, useImportProjectPlanDraft, useProjectPlanDraft } from '@hooks/api/projectPlanDrafts';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { roleRank, TEMPLATE_ACTIONS_MIN_RANK } from '@constants/roleHierarchy';
import { useTranslation } from '@hooks/useTranslation';
import {
  PROJECT_FORM_STEP_FIELDS,
  PROJECT_STATE_DEFINITIONS,
  ProjectFormValues,
  buildProjectInput,
  buildProjectPayload,
  buildProjectValidationSchema,
  formValuesFromProjectInput,
  sameProjectInput,
} from '../utils/projectForm';

type PlanOption = 'manual' | 'template' | 'ai';

const STEP_KEYS = ['basics', 'schedule', 'team', 'budget', 'plan'] as const;

export const ProjectWizardPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const t = useTranslation('projectWizard');
  const tProjects = useTranslation('projects');
  const tActionMessages = useTranslation('actionMessages');
  const tCommon = useTranslation('common');
  const tAiPlan = useTranslation('aiPlan');
  const { role } = useAppSelector((state: RootState) => state.user);

  const clientId = useMemo(() => new URLSearchParams(location.search).get('clientId') || '', [location.search]);
  const canUseTemplateActions = roleRank(role) >= TEMPLATE_ACTIONS_MIN_RANK;
  const { data: aiPlanningAvailable } = useAiPlanningAvailable();

  // Sprint 36, story-189: a `?draft=` in the URL means we are resuming an AI plan, so open on the
  // plan step with the AI option already chosen.
  const initialDraftId = new URLSearchParams(location.search).get('draft') || undefined;
  const [stepIndex, setStepIndex] = useState(initialDraftId ? STEP_KEYS.length - 1 : 0);
  const [siteImageFiles, setSiteImageFiles] = useState<File[]>([]);
  const [planOption, setPlanOption] = useState<PlanOption | null>(initialDraftId ? 'ai' : null);
  const [isSelectTemplateModalOpen, setIsSelectTemplateModalOpen] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [aiDraftId, setAiDraftId] = useState<string | undefined>(initialDraftId);

  // Follow the URL when it moves to another draft (reopened from the in-progress list). The id is
  // per-draft URL state, so concurrent drafts never share it.
  const urlDraftId = new URLSearchParams(location.search).get('draft') || undefined;
  useEffect(() => {
    if (urlDraftId && urlDraftId !== aiDraftId) {
      setAiDraftId(urlDraftId);
      setPlanOption('ai');
      setStepIndex(STEP_KEYS.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlDraftId]);

  // Write the draft id into the URL as soon as it exists, so a refresh resumes the same draft.
  const handleDraftIdChange = (nextDraftId: string | undefined) => {
    setAiDraftId(nextDraftId);
    const params = new URLSearchParams(location.search);
    if (nextDraftId) params.set('draft', nextDraftId);
    else params.delete('draft');
    history.replace({ search: params.toString() });
  };

  const { data: clients } = useClients();
  const { data: projectManagersData } = useEmployees({ role: 'PROJECT_MANAGER', limit: 100 });
  const { data: superintendentsData } = useEmployees({ role: 'SUPERINTENDENT', limit: 100 });
  const { data: reviewersData } = useEmployees({ role: 'REVIEWER', limit: 100 });

  const createProjectMutation = useCreateProject();
  const importTaskTemplateMutation = useImportTaskTemplate();
  const importPlanDraftMutation = useImportProjectPlanDraft();

  const states = useMemo(
    () => PROJECT_STATE_DEFINITIONS.map((s) => ({ id: s.id, name: tProjects[s.nameKey], backendValue: s.backendValue })),
    [tProjects],
  );
  const validationSchema = useMemo(() => buildProjectValidationSchema(tProjects), [tProjects]);

  const formik = useFormik<ProjectFormValues>({
    initialValues: {
      name: '',
      address: '',
      description: '',
      client: clientId,
      budget: 0,
      totalManHours: '',
      startDate: '',
      endDate: '',
      lat: '',
      long: '',
      state: 'ongoing',
      projectManagerId: '',
      superintendentIds: [],
      reviewerEmployeeIds: [],
    },
    validationSchema,
    onSubmit: () => undefined,
  });

  const { values, errors, touched, handleChange, setFieldValue, setTouched, validateForm } = formik;

  const steps: WizardStep[] = [
    { key: 'basics', label: t.stepBasics },
    { key: 'schedule', label: t.stepSchedule },
    { key: 'team', label: t.stepTeam },
    { key: 'budget', label: t.stepBudget },
    { key: 'plan', label: t.stepPlan },
  ];

  // Walkthrough fix (sprint 38): the wizard's details are stored with the AI draft
  // (`projectInput`), so once a draft exists they are saved — leaving (even mid-generation) loses
  // nothing and the draft resumes with the form intact. A resumed draft restores them here, once
  // per draft id, and lands on the plan step ready to create the project.
  const { data: aiDraft } = useProjectPlanDraft(planOption === 'ai' ? aiDraftId : undefined);
  const restoredForDraftRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!aiDraft?.projectInput || !aiDraftId || aiDraft.draftId !== aiDraftId) return;
    if (restoredForDraftRef.current === aiDraftId) return;
    restoredForDraftRef.current = aiDraftId;
    formik.resetForm({ values: formValuesFromProjectInput(aiDraft.projectInput, formik.initialValues) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDraft?.draftId, aiDraft?.projectInput, aiDraftId]);

  const currentProjectInput = buildProjectInput(values, states);
  // Saved with the draft = a draft exists and (while it loads, or once loaded) holds these exact
  // details. A legacy draft without `projectInput`, or details edited after generation, are not.
  const formSavedWithDraft =
    planOption === 'ai' &&
    !!aiDraftId &&
    (!aiDraft || aiDraft.draftId !== aiDraftId || (!!aiDraft.projectInput && sameProjectInput(aiDraft.projectInput, currentProjectInput)));

  // Site images cannot be stored with a draft; they are kept while in memory and otherwise
  // skipped, never a reason to block leaving once the details are saved.
  const isDirty = formSavedWithDraft
    ? false
    : formik.dirty || siteImageFiles.length > 0 || planOption === 'manual' || planOption === 'template';

  const goNext = async (): Promise<boolean> => {
    const stepKey = STEP_KEYS[stepIndex];
    const fields = PROJECT_FORM_STEP_FIELDS[stepKey as keyof typeof PROJECT_FORM_STEP_FIELDS];
    if (!fields) {
      setStepIndex((i) => Math.min(i + 1, steps.length - 1));
      return true;
    }
    const stepErrors = await validateForm();
    const touchedPatch: Record<string, boolean> = {};
    fields.forEach((f) => (touchedPatch[f] = true));
    setTouched({ ...touched, ...touchedPatch }, false);
    const hasStepError = fields.some((f) => stepErrors[f as keyof typeof stepErrors]);
    if (hasStepError) return false;
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    return true;
  };

  const goBack = () => setStepIndex((i) => Math.max(i - 1, 0));

  const buildCreatePayload = () => {
    const payload = buildProjectPayload(values, states);
    const selectedFile = siteImageFiles[0];
    return selectedFile ? { ...payload, siteImage: selectedFile } : payload;
  };

  const handleCreateManual = async () => {
    setIsCommitting(true);
    try {
      const project = await createProjectMutation.mutateAsync(buildCreatePayload() as any);
      history.push(`/schedule/${project.projectId}?newTask=1`);
    } catch (e) {
      // useCreateProject's own onError toast already surfaced the failure.
    } finally {
      setIsCommitting(false);
    }
  };

  const handleTemplateSelect = async (template: TaskTemplate, adjustEndDates: boolean) => {
    setIsSelectTemplateModalOpen(false);
    setIsCommitting(true);
    try {
      const project = await createProjectMutation.mutateAsync(buildCreatePayload() as any);
      try {
        await importTaskTemplateMutation.mutateAsync({
          templateId: template.templateId,
          projectId: project.projectId!,
          startDate: values.startDate,
          projectEndDate: values.endDate,
          adjustEndDates,
        });
      } catch (importError) {
        toast.error(tActionMessages.templateImportFailedAfterCreate);
      }
      history.push(`/schedule/${project.projectId}`);
    } finally {
      setIsCommitting(false);
    }
  };

  const handleCommitAiPlan = async () => {
    // A resumed draft restores its stored details; only a legacy draft without them (or a form
    // made invalid since) sends the user back to fill them in rather than failing the create.
    const formErrors = await validateForm();
    if (Object.keys(formErrors).length > 0) {
      const allTouched: Record<string, boolean> = {};
      Object.keys(values).forEach((k) => (allTouched[k] = true));
      setTouched(allTouched, false);
      toast.info(tAiPlan.resumeFillDetails);
      setStepIndex(0);
      return;
    }
    setIsCommitting(true);
    try {
      const project = await createProjectMutation.mutateAsync(buildCreatePayload() as any);
      if (aiDraftId) {
        try {
          await importPlanDraftMutation.mutateAsync({
            draftId: aiDraftId,
            projectId: project.projectId!,
            startDate: values.startDate,
            projectEndDate: values.endDate,
            adjustEndDates: false,
          });
        } catch (importError) {
          toast.error(tActionMessages.templateImportFailedAfterCreate);
        }
      }
      history.push(`/schedule/${project.projectId}`);
    } finally {
      setIsCommitting(false);
    }
  };

  const clientOptions: SelectOption[] =
    clients?.filter((c) => c.clientId).map((c) => ({ label: c.name, value: c.clientId! })) || [];
  const projectManagerOptions: SelectOption[] =
    projectManagersData?.employees
      ?.filter((e) => e.employeeId)
      .map((e) => ({ label: `${e.user?.firstName || ''} ${e.user?.lastName || ''}`.trim(), value: e.employeeId! })) || [];
  const superintendentOptions: SelectOption[] =
    superintendentsData?.employees
      ?.filter((e) => e.employeeId)
      .map((e) => ({ label: `${e.user?.firstName || ''} ${e.user?.lastName || ''}`.trim(), value: e.employeeId! })) || [];
  const reviewerOptions: SelectOption[] =
    reviewersData?.employees
      ?.filter((e) => e.employeeId)
      .map((e) => ({ label: `${e.user?.firstName || ''} ${e.user?.lastName || ''}`.trim(), value: e.employeeId! })) || [];

  return (
    <div className={`${stepIndex === STEP_KEYS.length - 1 && planOption === 'ai' && aiDraftId ? 'max-w-7xl' : 'max-w-4xl'} mx-auto py-6 px-4`}>
      {/* Only warn when leaving the wizard: writing `?draft=` stays on this page and must not prompt. */}
      <Prompt
        when={isDirty && !isCommitting}
        message={(next) => (next.pathname === location.pathname ? true : t.discardBody)}
      />
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={() => history.push('/projects')} className="text-gray-500 hover:text-gray-800 dark:hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">{t.title}</h1>
        <span className="text-xs text-gray-400 ml-auto" data-testid="wizard-step-progress">
          {tCommon.wizardStepOf.replace('{n}', String(stepIndex + 1)).replace('{total}', String(steps.length))}
        </span>
      </div>

      <Wizard
        steps={steps}
        currentStep={stepIndex}
        onStepChange={setStepIndex}
        onNext={goNext}
        onBack={goBack}
        nextLabel={t.next}
        backLabel={t.previous}
        hideFooter={stepIndex === steps.length - 1}>
        {stepIndex === 0 && (
          <div className="flex flex-col gap-4">
            {/* Walkthrough fix (sprint 38): resumable AI plans surface at the start of the wizard. */}
            {canUseTemplateActions && aiPlanningAvailable && <InProgressAiPlans variant="expanded" excludeDraftId={aiDraftId} />}
            <FormGroup error={errors.name} htmlFor="name" label={tProjects.name}>
              <Input name="name" value={values.name} onChange={handleChange} error={!!(touched.name && errors.name)} data-testid="wizard-name-input" />
            </FormGroup>
            <FormGroup error={errors.state} htmlFor="state" label={tProjects.stateLabel}>
              <ReactSelect
                name="state"
                value={states.map((s) => ({ label: s.name, value: s.id })).find((o) => o.value === values.state)}
                onChange={(opt) => setFieldValue('state', (opt as SelectOption | null)?.value || 'ongoing')}
                options={states.map((s) => ({ label: s.name, value: s.id }))}
                isSearchable
              />
            </FormGroup>
            <FormGroup htmlFor="description" label={tProjects.descriptionLabel}>
              <TextareaInput name="description" rows={4} value={values.description} onChange={handleChange} />
            </FormGroup>
            <FileDropZone
              files={siteImageFiles}
              onFilesChange={(files) => setSiteImageFiles(files.slice(-1))}
              multiple={false}
              maxFiles={1}
              acceptedTypes="images"
              avatar
              title={tProjects.siteImageDropTitle}
              subtitle={tProjects.siteImageDropSubtitle}
            />
          </div>
        )}

        {stepIndex === 1 && (
          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <FormGroup error={errors.startDate} htmlFor="startDate" label={tProjects.startDateLabel}>
                <Input type="date" name="startDate" value={values.startDate} onChange={handleChange} error={!!(touched.startDate && errors.startDate)} data-testid="wizard-start-date-input" />
              </FormGroup>
              <FormGroup error={errors.endDate} htmlFor="endDate" label={tProjects.endDateLabel}>
                <Input type="date" name="endDate" value={values.endDate} onChange={handleChange} error={!!(touched.endDate && errors.endDate)} data-testid="wizard-end-date-input" />
              </FormGroup>
            </div>
            <FormGroup error={errors.address} htmlFor="address" label={tProjects.address}>
              <GoogleAddressInput
                name="address"
                value={values.address}
                onChange={(value) => setFieldValue('address', value)}
                onChangeCoor={(lat, long) => {
                  setFieldValue('lat', lat?.toString() || '');
                  setFieldValue('long', long?.toString() || '');
                }}
              />
            </FormGroup>
          </div>
        )}

        {stepIndex === 2 && (
          <div className="flex flex-col gap-4">
            <FormGroup htmlFor="projectManagerId" label={tProjects.projectManagerLabel}>
              <ReactSelect
                name="projectManagerId"
                onChange={(opt) => setFieldValue('projectManagerId', (opt as SelectOption | null)?.value || '')}
                value={projectManagerOptions.find((o) => o.value === values.projectManagerId)}
                options={projectManagerOptions}
                isSearchable
                isClearable
              />
            </FormGroup>
            <FormGroup htmlFor="superintendentIds" label={tProjects.superintendentsLabel}>
              <ReactSelect
                isMulti
                name="superintendentIds"
                onChange={(opts) => setFieldValue('superintendentIds', Array.isArray(opts) ? opts.map((o: any) => o.value) : [])}
                value={superintendentOptions.filter((o) => values.superintendentIds.includes(o.value as string))}
                options={superintendentOptions}
                isSearchable
              />
            </FormGroup>
            <FormGroup htmlFor="reviewerEmployeeIds" label={tProjects.reviewersLabel}>
              <ReactSelect
                isMulti
                name="reviewerEmployeeIds"
                onChange={(opts) => setFieldValue('reviewerEmployeeIds', Array.isArray(opts) ? opts.map((o: any) => o.value) : [])}
                value={reviewerOptions.filter((o) => values.reviewerEmployeeIds.includes(o.value as string))}
                options={reviewerOptions}
                isSearchable
              />
            </FormGroup>
            <FormGroup htmlFor="client" label={tProjects.clientLabel}>
              <ReactSelect
                name="client"
                onChange={(opt) => setFieldValue('client', (opt as SelectOption | null)?.value || '')}
                value={clientOptions.find((o) => o.value === values.client)}
                options={clientOptions}
                isSearchable
                isDisabled={!!clientId}
              />
            </FormGroup>
          </div>
        )}

        {stepIndex === 3 && (
          <div className="flex gap-4">
            <FormGroup htmlFor="budget" label={tProjects.budgetLabel}>
              <InputCurrency currency="USD" name="budget" value={values.budget} onValueChange={(v) => setFieldValue('budget', v || 0)} />
            </FormGroup>
            <FormGroup error={errors.totalManHours} htmlFor="totalManHours" label={tProjects.totalManHoursLabel}>
              <Input type="number" min={0} name="totalManHours" value={values.totalManHours} onChange={handleChange} />
            </FormGroup>
          </div>
        )}

        {stepIndex === 4 && (
          <div className="flex flex-col gap-4">
            {!planOption && (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-300">{t.planQuestion}</p>
                {canUseTemplateActions && aiPlanningAvailable && <InProgressAiPlans showWhenEmpty />}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={() => setPlanOption('manual')}
                    data-testid="plan-option-manual"
                    className="flex-1 text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{t.planOptionManualTitle}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.planOptionManualBody}</p>
                  </button>
                  {canUseTemplateActions && (
                    <button
                      type="button"
                      onClick={() => setPlanOption('template')}
                      data-testid="plan-option-template"
                      className="flex-1 text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{t.planOptionTemplateTitle}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.planOptionTemplateBody}</p>
                    </button>
                  )}
                  {canUseTemplateActions && aiPlanningAvailable && (
                    <button
                      type="button"
                      onClick={() => setPlanOption('ai')}
                      data-testid="plan-option-ai"
                      className="flex-1 text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{t.planOptionAiTitle}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t.planOptionAiBody}</p>
                    </button>
                  )}
                </div>
              </>
            )}

            {planOption === 'manual' && (
              <div className="flex justify-between items-center">
                <button type="button" onClick={() => setPlanOption(null)} className="text-sm text-gray-500">
                  {t.backToOptions}
                </button>
                <button
                  type="button"
                  onClick={handleCreateManual}
                  disabled={isCommitting}
                  data-testid="wizard-create-project"
                  className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-white disabled:opacity-60">
                  {t.createProject}
                </button>
              </div>
            )}

            {planOption === 'template' && (
              <div className="flex justify-between items-center">
                <button type="button" onClick={() => setPlanOption(null)} className="text-sm text-gray-500">
                  {t.backToOptions}
                </button>
                <button
                  type="button"
                  onClick={() => setIsSelectTemplateModalOpen(true)}
                  disabled={isCommitting}
                  className="px-5 py-2 rounded-lg text-sm font-medium bg-primary text-white disabled:opacity-60">
                  {t.chooseTemplate}
                </button>
              </div>
            )}

            {planOption === 'ai' && (
              <div>
                <button type="button" onClick={() => setPlanOption(null)} className="text-sm text-gray-500 mb-3">
                  {t.backToOptions}
                </button>
                <AiPlanGenerator
                  context={{ kind: 'wizard' }}
                  draftId={aiDraftId}
                  onDraftIdChange={handleDraftIdChange}
                  onCommitPlan={handleCommitAiPlan}
                  onCancel={() => setPlanOption(null)}
                  isCommitting={isCommitting}
                  projectInput={currentProjectInput}
                />
              </div>
            )}
          </div>
        )}
      </Wizard>

      <SelectTemplateModal
        isOpen={isSelectTemplateModalOpen}
        onClose={() => setIsSelectTemplateModalOpen(false)}
        onSelect={handleTemplateSelect}
        projectStartDate={values.startDate}
        projectEndDate={values.endDate}
        title={tProjects.selectTemplateModalTitle}
        confirmText={tProjects.createWithTemplateButton}
      />
    </div>
  );
};

export default ProjectWizardPage;
